import type { NotificationItem } from '@/types';

import React from 'react';
import { RiAddLine, RiCheckLine, RiCheckboxCircleLine, RiNotificationLine } from '@remixicon/react';
import { Button, Tooltip } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import {
  notificationsListParamsAtom,
  notificationsQueryAtom,
  markNotificationRead,
  markAllNotificationsRead
} from '@/store';

// Custom hooks
import { useNotificationsFilters, useNotificationsSorting } from '@/hooks';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { NotificationTypeChip, NotificationStatusChip } from '@/components/notifications';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { UserNotificationModal } from '@/components/users/UserNotificationModal';
import { SearchInput } from '@/components/shared/SearchInput';

export default function NotificationsPage() {
  const { data, isLoading, isError, error } = useAtomValue(notificationsQueryAtom);
  const [params, setParams] = useAtom(notificationsListParamsAtom);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    typeFilter,
    statusFilter,
    hasAnyFilter,
    handleSearch,
    handleClearSearch,
    clearFilters,
    handleTypeChange,
    handleStatusChange,
    handlePageChange
  } = useNotificationsFilters();

  const notifications = React.useMemo<NotificationItem[]>(() => data?.data ?? [], [data]);
  const { sortDescriptor, handleSortChange, sortedNotifications } = useNotificationsSorting(notifications);
  const [isNotificationModalOpen, setNotificationModalOpen] = React.useState(false);
  const openNotificationModal = React.useCallback(() => {
    setNotificationModalOpen(true);
  }, []);
  const handleNotificationModalChange = React.useCallback((open: boolean) => {
    setNotificationModalOpen(open);
  }, []);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Mark single notification as read
  const [, markAsRead] = useAtom(markNotificationRead);
  const handleMarkAsRead = React.useCallback(
    async (notificationId: string) => {
      try {
        await markAsRead(notificationId);
        triggerRefresh();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [markAsRead, triggerRefresh]
  );

  // Mark all notifications as read
  const [, markAllAsRead] = useAtom(markAllNotificationsRead);
  const handleMarkAllAsRead = React.useCallback(async () => {
    try {
      await markAllAsRead();
      triggerRefresh();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [markAllAsRead, triggerRefresh]);

  const renderCell = React.useCallback(
    (notification: NotificationItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'id':
          return <div className="text-default-500 font-mono text-sm">#{notification.id}</div>;
        case 'message':
          return (
            <div className="max-w-xs">
              <p className="text-foreground line-clamp-2 text-sm">{notification.message}</p>
            </div>
          );
        case 'target':
          return (
            <div className="text-sm">
              {notification.target_user && (
                <div className="text-default-600">
                  User: {notification.target_user.first_name} {notification.target_user.last_name}
                </div>
              )}
              {notification.target_organization && (
                <div className="text-default-600">Workspace: {notification.target_organization.name}</div>
              )}
              {!notification.target_user && !notification.target_organization && (
                <span className="text-default-400">System</span>
              )}
            </div>
          );
        case 'type':
          return <NotificationTypeChip type={notification.type} />;
        case 'status':
          return <NotificationStatusChip status={notification.status} />;
        case 'created_at':
          return <DateTimeDisplay dateString={notification.created_at} />;
        case 'actions':
          return (
            <div className="flex items-center gap-2">
              {notification.status === 'unread' && (
                <Tooltip content="Mark as read">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="primary"
                    onPress={() => handleMarkAsRead(notification.id)}
                  >
                    <RiCheckLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              )}
            </div>
          );
        default:
          return null;
      }
    },
    [handleMarkAsRead]
  );

  const renderCollapseContent = React.useCallback(
    (notification: NotificationItem) => (
      <div className="space-y-3 p-4">
        <div>
          <h4 className="mb-2 text-sm font-medium">Full Message</h4>
          <p className="text-default-600 text-sm whitespace-pre-wrap">{notification.message}</p>
        </div>

        {notification.metadata && (
          <div>
            <h4 className="mb-2 text-sm font-medium">Metadata</h4>
            <JsonViewer data={notification.metadata} maxHeightPx={200} />
          </div>
        )}

        <div className="border-default-200 flex items-center justify-between border-t pt-2">
          <div className="text-default-500 text-xs">
            Created: <DateTimeDisplay dateString={notification.created_at} showSeconds />
            {notification.read_at && (
              <>
                <br />
                Read: <DateTimeDisplay dateString={notification.read_at} showSeconds />
              </>
            )}
          </div>

          {notification.status === 'unread' && (
            <Button size="sm" color="primary" variant="flat" onPress={() => handleMarkAsRead(notification.id)}>
              <RiCheckboxCircleLine className="mr-1 h-4 w-4" />
              Mark as Read
            </Button>
          )}
        </div>
      </div>
    ),
    [handleMarkAsRead]
  );

  const columns = [
    { name: 'ID', uid: 'id', sortable: true },
    { name: 'Message', uid: 'message', sortable: false },
    { name: 'Target', uid: 'target', sortable: false },
    { name: 'Type', uid: 'type', sortable: true },
    { name: 'Status', uid: 'status', sortable: true },
    { name: 'Created', uid: 'created_at', sortable: true },
    { name: 'Actions', uid: 'actions', sortable: false }
  ];

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  const unreadCount = notifications.filter((n) => n.status === 'unread').length;

  return (
    <PermissionGuard permission="Notification.index">
      <TablePage
        title={
          <span className="flex items-center gap-2">
            <RiNotificationLine className="h-7 w-7 text-primary" />
            <span>Notifications</span>
          </span>
        }
        data={sortedNotifications}
        isLoading={isLoading}
        isError={isError}
        error={error}
        columns={columns}
        headerColumns={headerColumns}
        sortDescriptor={sortDescriptor}
        onSortChange={handleSortChange}
        renderCell={renderCell as any}
        enableCollapse={true}
        renderCollapseContent={renderCollapseContent as any}
        currentPage={data?.meta?.current_page}
        totalPages={data?.meta?.last_page}
        onPageChange={handlePageChange}
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={clearFilters}
            onRefresh={triggerRefresh}
            rightSection={
              unreadCount > 0 ? (
                <Button color="primary" variant="flat" size="sm" onPress={handleMarkAllAsRead}>
                  <RiCheckboxCircleLine className="mr-1 h-4 w-4" />
                  Mark All Read ({unreadCount})
                </Button>
              ) : undefined
            }
          >
            <SearchInput
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onValueChange={setSearchTerm}
              onSearch={handleSearch}
              onClear={handleClearSearch}
                  className="w-64"
                />

            <ColumnsToggleDropdown
              allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
              selected={visibleColumns}
              onChange={(next) => setVisibleColumns(next)}
            />

                <FilterSelect
                  label="Type"
                  value={typeFilter}
                  onChange={handleTypeChange}
                  options={[
                    { key: 'all', label: 'All Types' },
                    { key: 'system', label: 'System' },
                    { key: 'experiment', label: 'Experiment' },
                    { key: 'alert', label: 'Alert' },
                    { key: 'admin_manual_broadcast_sent', label: 'Broadcast Sent' },
                    { key: 'admin_system_alert', label: 'Admin Alert' }
                  ]}
                />

                <FilterSelect
                  label="Status"
                  value={statusFilter}
                  onChange={handleStatusChange}
                  options={[
                    { key: 'all', label: 'All Status' },
                    { key: 'read', label: 'Read' },
                    { key: 'unread', label: 'Unread' }
                  ]}
                />
          </TableFilters>
        }
        titleActions={
          <PermissionGuard permission="Notification.send">
            <Button
              color="success"
              size="sm"
              variant="flat"
              startContent={<RiAddLine className="h-4 w-4" />}
              onPress={openNotificationModal}
            >
              Add Notification
            </Button>
          </PermissionGuard>
        }
      >
        <UserNotificationModal
          isOpen={isNotificationModalOpen}
          onOpenChange={handleNotificationModalChange}
          targetUser={null}
          onSendSuccess={triggerRefresh}
      />
      </TablePage>
    </PermissionGuard>
  );
}
